import React, { useCallback, useEffect, useState } from "react";
import { ReactComponent as InfoIcon } from "../../../../Assets/ActionIcons/InfoIcon.svg";
import { useSettingsStore } from "../../../../Store/Settings";
import { usePagination, PAGINATION_KEYS } from "../../../../Store/Pagination";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from "react-router-dom";
import { useCustomerStore } from "../../../../Store/Customer";
import { PaymentsAutoCollectFilterValue } from "../../../../Contracts/Settings";
import Table, { Column, DataSourceItem } from "../../../../Components/Atoms/Table";
import TablePaginationFooter from "../../../../Components/Atoms/Table/TablePaginationFooter";
import { createSortFunction } from "../../../../Utils/TableUtils";
import CustomFieldsListActions from "./CustomFieldsListActions";
import { capitalizeAfterUnderscore } from "../../../../Contracts/Common";
import Modal from "../../../../Components/Atoms/Modal";

const CustomerDetailCustomFieldsTab: React.FC = () => {
  const {
    settings,
    isLoading,
    getPaymentAutoCollectFilterValues,
    createPaymentAutoCollectFilterValues,
    paymentAutoCollectFilterValues,
    paymentAutoCollectFilterValuesCount
  } = useSettingsStore();
  const [customFilterAddModalOpen, setCustomFilterAddModalOpen] = React.useState<boolean>(false);
  const [filterValue, setFilterValue] = React.useState<string>("");

  const { limit, page, setLimit, setPage } = usePagination(
    PAGINATION_KEYS.CUSTOMER_DETAIL_CUSTOM_FIELDS,
    { limit: 10, page: 1 }
  );

  const [sortParamValue, setSortParamValue] = useState<string>("created_at:desc");
  const [errorMessage, setErrorMessage] = React.useState<string>("");

  const { customerDetailEntity } = useCustomerStore();
  const { getAccessTokenSilently } = useAuth0();
  const { id: customerId } = useParams();

  const columns: Column<PaymentsAutoCollectFilterValue>[] = [
    {
      title: "Custom Filter Value",
      dataIndex: "filter",
      key: "filter",
      sortClicked: createSortFunction("filter", setSortParamValue)
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: "w-12",
      render: d => <CustomFieldsListActions filter={d.data} />
    }
  ];

  const refreshThings = useCallback(async () => {
    if (customerId) {
      await getPaymentAutoCollectFilterValues(
        getAccessTokenSilently,
        customerId,
        limit,
        sortParamValue,
        page
      );
    } else {
      throw new Error("Customer ID not found");
    }
  }, [
    getAccessTokenSilently,
    getPaymentAutoCollectFilterValues,
    customerId,
    limit,
    page,
    sortParamValue
  ]);

  useEffect(() => {
    refreshThings();
  }, [refreshThings, limit, page, sortParamValue]);

  const saveCustomFilter = async () => {
    if (!filterValue.trim()) {
      setErrorMessage("Custom Filter Value cannot be empty.");
      return;
    }
    if (customerId) {
      await createPaymentAutoCollectFilterValues(
        getAccessTokenSilently,
        parseInt(customerId),
        filterValue
      );
      setCustomFilterAddModalOpen(false);
      setFilterValue("");
      setErrorMessage("");
    } else {
      throw new Error("Customer ID not found");
    }
  };

  const autoCollectEnabled = !!customerDetailEntity?.auto_collection_enabled_date;

  const filterValuesForCustomer = customerId
    ? paymentAutoCollectFilterValues[parseInt(customerId)] || []
    : [];

  const filterValuesCountForCustomer = customerId
    ? paymentAutoCollectFilterValuesCount[parseInt(customerId)]
    : 0;

  const dataSource: DataSourceItem<PaymentsAutoCollectFilterValue>[] = filterValuesForCustomer.map(
    filter => ({
      key: filter.id.toString(),
      filter: filter.filter,
      data: filter
    })
  );

  return (
    <div className="relative flex flex-col h-[calc(100vh-12rem)]">
      <button
        disabled={!autoCollectEnabled}
        onClick={() => setCustomFilterAddModalOpen(true)}
        className={`absolute bottom-full mb-1 right-0 ${
          autoCollectEnabled
            ? "bg-blue-600 hover:bg-blue-500 text-white cursor-pointer"
            : "bg-white-smoke text-gray-600 cursor-not-allowed border"
        } rounded p-2 text-sm`}
      >
        Add Custom Filter Field
      </button>
      {autoCollectEnabled ? (
        <div className="flex items-center my-2 p-4 space-x-2 bg-panache border border-apple rounded">
          <InfoIcon className="w-4 h-4 text-gray-600 inline-block mr-2" />
          <div>
            If the{" "}
            <i>
              {capitalizeAfterUnderscore(settings?.selected_custom_field?.replace("custom::", ""))}
            </i>{" "}
            field does not match any of the Custom Filter Fields, Auto Collect will be{" "}
            <i>
              {settings?.payment_default_auto_collect_based_on_custom_field
                ? "enabled"
                : "disabled"}
            </i>{" "}
            for any incoming invoice. When the{" "}
            <i>
              {capitalizeAfterUnderscore(settings?.selected_custom_field?.replace("custom::", ""))}
            </i>{" "}
            field matches any of these filters, Auto Collect will be{" "}
            <i>
              {settings?.payment_default_auto_collect_based_on_custom_field
                ? "disabled"
                : "enabled"}
            </i>
            .
          </div>
        </div>
      ) : (
        <div className="flex items-center my-2 p-4 space-x-2 bg-white-smoke border rounded">
          <InfoIcon className="w-4 h-4 text-gray-600 inline-block mr-2" />
          <div>
            Please note that the Custom Filter Fields are disabled unless you enable Auto Collect
            for this client.
          </div>
        </div>
      )}
      <Modal
        isOpen={customFilterAddModalOpen}
        onClose={() => setCustomFilterAddModalOpen(false)}
        title="Add Custom Filter"
        onOk={saveCustomFilter}
        onCancel={() => setCustomFilterAddModalOpen(false)}
        topOffset="10rem"
      >
        <div className="p-4">
          The following Custom Filter values will determine the auto-collect behaviour for all
          invoices for this client.
        </div>
        <div className="px-4 pb-4 grid grid-cols-5 gap-4 items-center">
          <label className="col-span-2 font-semibold">Custom Filter Value</label>
          <div className="col-span-3">
            <input
              type="text"
              value={filterValue}
              onChange={e => {
                setFilterValue(e.target.value.slice(0, 50));
                setErrorMessage("");
              }}
              className="w-full border rounded p-2"
              placeholder="Enter Custom Filter Value"
            />
            {errorMessage && <div className="text-red-600 text-sm mt-1">{errorMessage}</div>}
          </div>
        </div>
      </Modal>
      <div className="flex flex-grow flex-col overflow-y-auto">
        <Table columns={columns} dataSource={dataSource} isLoading={isLoading} />
        {filterValuesCountForCustomer === 0 && (
          <div className="text-center p-4">No Custom Filter Fields added yet.</div>
        )}
      </div>
      <TablePaginationFooter
        limit={limit}
        setLimit={setLimit}
        totalRecords={filterValuesCountForCustomer}
        page={page}
        setPage={setPage}
        isLoading={isLoading}
      />
    </div>
  );
};

export default CustomerDetailCustomFieldsTab;
